import * as amplitude from "@amplitude/analytics-browser";
import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import { Currency, isCurrency } from "@redotech/money/currencies";
import { moneyFormat } from "@redotech/money/util";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { LoadState, useLoad } from "@redotech/react-util/load";
import { LocalizationProvider } from "@redotech/redo-i18n";
import { AuthContext } from "@redotech/redo-merchant-app-common/auth";
import { PermissionsContext } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { getCurrentTeam } from "@redotech/redo-merchant-app-common/client/team";
import { getUser } from "@redotech/redo-merchant-app-common/client/user";
import { RedoPlatformAuthRpcClientContext } from "@redotech/redo-merchant-app-common/platform/redo-platform-rpc-client-provider";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import {
  OnboardingAverageProductWeightContext,
  StorefrontClientContext,
} from "@redotech/redo-merchant-app-common/team";
import {
  ReloadUserContext,
  UserContext,
} from "@redotech/redo-merchant-app-common/user";
import { UserPermissions } from "@redotech/redo-model/permissions";
import {
  ReloadTeamContext,
  TeamContext,
} from "@redotech/redo-web/context/team-context";
import {
  CurrencyContext,
  DEFAULT_CURRENCY,
  default_format_Currency,
} from "@redotech/redo-web/currency";
import { ShopifyStorefrontClient } from "@redotech/shopify-storefront";
import {
  ReactNode,
  memo,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getOnboarding } from "../api/onboarding";
import { FxProvider } from "./fx-provider";

export const TeamProvider = memo(function TeamProvider({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const client = useRequiredContext(RedoMerchantClientContext);
  const auth = useContext(AuthContext);

  const [teamTrigger, setTeamTrigger] = useState(Symbol());
  const teamLoad = useLoad(
    (signal) =>
      auth ? getCurrentTeam(client, { signal }) : Promise.resolve(undefined),
    [auth, client, teamTrigger],
  );

  const [userTrigger, setUserTrigger] = useState(Symbol());
  const userLoad = useLoad(
    (signal) => {
      const userToReturn = auth
        ? getUser(client, { signal })
        : Promise.resolve(undefined);
      return userToReturn;
    },
    [auth, client, userTrigger],
  );

  const [storefrontClient, setStorefrontClient] =
    useState<ShopifyStorefrontClient>();

  const reloadTeam = useHandler(() => setTeamTrigger(Symbol()));
  const reloadUser = useHandler(() => setUserTrigger(Symbol()));

  const userID = userLoad.value?._id;
  useEffect(() => {
    amplitude.setUserId(userID);
  }, [userID]);

  const userEmail = userLoad.value?.email;
  useEffect(() => {
    if (typeof userEmail === "string") {
      const identifyEvent = new amplitude.Identify();
      identifyEvent.set("email", userEmail);
      amplitude.identify(identifyEvent);
      datadogLogs.setUser({ email: userEmail });
      datadogRum.setUser({ email: userEmail });
    }
  }, [userEmail]);

  // Attach the merchant's install date and approximate tenure as Amplitude user
  // properties so events (e.g. onboarding-wizard clicks) can be segmented by how
  // long the team has been on Redo. `daysSinceInstall` is snapshotted at load
  // time and is intended for coarse cohort filtering, not exact reporting;
  // `installDate` is the source of truth if precise math is needed.
  const teamCreatedAt = teamLoad.value?.team.createdAt;
  useEffect(() => {
    if (teamCreatedAt) {
      const daysSinceInstall = Temporal.Instant.from(teamCreatedAt)
        .toZonedDateTimeISO("UTC")
        .toPlainDate()
        .until(Temporal.Now.zonedDateTimeISO("UTC").toPlainDate())
        .total("day");
      const identifyEvent = new amplitude.Identify();
      identifyEvent.set("daysSinceInstall", daysSinceInstall);
      identifyEvent.set("installDate", teamCreatedAt);
      amplitude.identify(identifyEvent);
    }
  }, [teamCreatedAt]);

  const rpcClient = useContext(RedoMerchantRpcClientContext);
  const [currency, setCurrency] = useState<Currency>(DEFAULT_CURRENCY);

  const currencyLoad = useLoad(async () => {
    const shopCurrencyStr: Currency =
      teamLoad.value?.team._shopify.currency || Currency.USD;

    const shopCurrency =
      shopCurrencyStr && isCurrency(shopCurrencyStr || Currency.USD)
        ? shopCurrencyStr
        : Currency.USD;

    const formatCurrency = (amount: number) =>
      moneyFormat({ amount, currency: shopCurrency });

    let usdRates: Record<string, number> | undefined;
    if (shopCurrency !== Currency.USD && rpcClient) {
      usdRates = await rpcClient.getExchangeRates(null);
    }

    const exchangeRate = usdRates?.[shopCurrency];
    const formatCurrencyFromUSD = exchangeRate
      ? (amount: number) =>
          moneyFormat({ amount: amount * exchangeRate, currency: shopCurrency })
      : (amount: number) => moneyFormat({ amount, currency: shopCurrency });

    return { currency: shopCurrency, formatCurrency, formatCurrencyFromUSD };
  }, [teamLoad]);

  const teamID = teamLoad.value?.team._id;
  useEffect(() => {
    if (teamID) {
      amplitude.setGroup("team", teamID);
      datadogLogs.setGlobalContextProperty("team", teamID);
      datadogRum.setGlobalContextProperty("team", teamID);
      if (
        teamLoad.value!.team.storeUrl &&
        teamLoad.value!.team.storefrontAccessToken
      ) {
        setStorefrontClient(
          new ShopifyStorefrontClient(
            teamLoad.value!.team.storeUrl,
            teamLoad.value!.team.storefrontAccessToken,
          ),
        );
      }
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamID]);

  const team = useMemo(
    () =>
      teamLoad.value?.team
        ? {
            ...teamLoad.value.team,
            featureFlags: teamLoad.value.featureFlags,
            platform: teamLoad.value.platform,
            updatePermissionsLink: teamLoad.value?.updatePermissionsLink,
            shopifyIntegration: teamLoad.value?.shopify,
            ianaTimezone: teamLoad.value?.team._shopify.iana_timezone,
          }
        : undefined,
    [teamLoad.value],
  );

  // Load onboarding data to get averageWeight
  const onboardingLoad = useLoad(
    (signal) =>
      auth && teamID
        ? getOnboarding(client, { signal, teamId: teamID })
        : Promise.resolve(undefined),
    [auth, client, teamID],
  );

  const averageProductWeight = useMemo(
    () => onboardingLoad.value?.averageWeight,
    [onboardingLoad.value],
  );

  const platformRpcClient = useContext(RedoPlatformAuthRpcClientContext);

  const loadedPermissions: LoadState<Set<UserPermissions> | undefined> =
    useLoad(async () => {
      if (!platformRpcClient) {
        return undefined;
      }
      const { permissions } = await platformRpcClient.getUserPermissions({});
      return new Set(permissions);
    }, [platformRpcClient]);

  return (
    <TeamContext.Provider value={team}>
      <ReloadTeamContext.Provider value={reloadTeam}>
        <CurrencyContext.Provider
          value={{
            currency:
              teamLoad.value?.team._shopify.currency ||
              currencyLoad.value?.currency ||
              currency,
            formatCurrency:
              currencyLoad.value?.formatCurrency || default_format_Currency,
            setCurrency,
            formatCurrencyFromUSD:
              currencyLoad.value?.formatCurrencyFromUSD ||
              default_format_Currency,
          }}
        >
          <UserContext.Provider value={userLoad.value}>
            <PermissionsContext.Provider value={loadedPermissions.value}>
              <ReloadUserContext.Provider value={reloadUser}>
                <StorefrontClientContext.Provider value={storefrontClient}>
                  <OnboardingAverageProductWeightContext.Provider
                    value={{ averageWeight: averageProductWeight }}
                  >
                    <LocalizationProvider
                      localization={team?.settings.localization}
                      preferredLocale={userLoad.value?.preferredLocale}
                    >
                      <FxProvider>{children}</FxProvider>
                    </LocalizationProvider>
                  </OnboardingAverageProductWeightContext.Provider>
                </StorefrontClientContext.Provider>
              </ReloadUserContext.Provider>
            </PermissionsContext.Provider>
          </UserContext.Provider>
        </CurrencyContext.Provider>
      </ReloadTeamContext.Provider>
    </TeamContext.Provider>
  );
});

export const TeamAndUserRequired = memo(function TeamAndUserRequired({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const team = useContext(TeamContext);
  const user = useContext(UserContext);

  if (!team || !user) {
    return null;
  } else {
    return <>{children}</>;
  }
});
